/**
 * SocialCapitalBoardPage — the compact, trophy-wall identity page.
 *
 * Design intent
 * -------------
 * The Passport (/passport/:handle) is a full dashboard.  This page is
 * a printable, screenshotable one-card identity — the recommended
 * default for people who use socialcapital.cc as their standalone
 * identity host WITHOUT maintaining a full SocialPulse profile.
 *
 *   • Portrait-oriented card (max-w-2xl) so it captures cleanly on
 *     mobile and desktop screenshots and prints to one page.
 *   • Trophy-wall aesthetic: earned badges are the hero, not a rail.
 *   • Level ring around the avatar signals tier at a glance.
 *   • Category dial is compact (an 11-slice conic gradient) instead
 *     of a full bar chart.
 *   • One featured endorsement quote — the most recent one — because
 *     a wall of them defeats the "board" aesthetic.
 *   • Copy-link + Share button in the top-right, no side nav noise.
 *   • Print-friendly CSS: hides top-nav and shares when printing.
 *
 * Route: /board/:handle    (canonical)
 *        /@:handle          (alias — nice URLs on socialcapital.cc)
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Award, Loader2, Heart, ScrollText, Feather, ArrowRight,
  Share2, Copy, TreeDeciduous, BookOpen, Crown, HandHeart, Recycle,
  Eye, Gift, ExternalLink, Trophy,
} from "lucide-react";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";

/* ------------------------------------------------------------------ */
/*  Category display metadata (aligned to the 8-bucket poster)         */
/* ------------------------------------------------------------------ */
const CATEGORY_META = {
  help_people:      { label: "Help People",       color: "#10B981" },
  save_food:        { label: "Save Food",         color: "#84CC16" },
  protect_nature:   { label: "Protect Nature",    color: "#EAB308" },
  support_farmers:  { label: "Support Farmers",   color: "#F97316" },
  share_knowledge:  { label: "Share Knowledge",   color: "#EF4444" },
  reuse_give:       { label: "Reuse & Give",      color: "#A855F7" },
  volunteer_serve:  { label: "Volunteer & Serve", color: "#3B82F6" },
  build_community:  { label: "Build Community",   color: "#14B8A6" },
};
const CATEGORY_ALIAS = {
  education: "share_knowledge", nature: "protect_nature",
  volunteer: "volunteer_serve", career: "help_people",
  innovation: "share_knowledge", arts: "share_knowledge",
  health: "help_people", culture: "share_knowledge",
  family: "help_people", community: "build_community", food: "save_food",
};
const normalizeCat = (k) => CATEGORY_ALIAS[k] || k;
const CATEGORY_ORDER = Object.keys(CATEGORY_META);

const ICONS = {
  TreeDeciduous, BookOpen, Crown, ScrollText, HandHeart, Award, Heart,
  Recycle, Eye, Gift, Feather, Sparkles,
};

const TIER_STYLE = {
  bronze: { bg: "linear-gradient(135deg,#f6ad55,#c05621)", ring: "#c05621" },
  silver: { bg: "linear-gradient(135deg,#cbd5e1,#64748b)", ring: "#64748b" },
  gold:   { bg: "linear-gradient(135deg,#fde68a,#b45309)", ring: "#b45309" },
};

// Level → outer-ring tier styling for the avatar
function levelRingStyle(level = "Helper") {
  const m = String(level).toLowerCase();
  if (m.includes("changemaker") || m.includes("ambassador")) return TIER_STYLE.gold;
  if (m.includes("leader") || m.includes("builder"))         return TIER_STYLE.silver;
  return TIER_STYLE.bronze;
}

/* ------------------------------------------------------------------ */
/*  Category dial — a compact conic-gradient pie of the 11 buckets     */
/* ------------------------------------------------------------------ */
function CategoryDial({ totals }) {
  const entries = CATEGORY_ORDER
    .map((k) => ({ k, v: Number(totals?.[k] || 0), color: CATEGORY_META[k].color }))
    .filter((e) => e.v > 0);
  const total = entries.reduce((s, e) => s + e.v, 0);

  const gradient = useMemo(() => {
    if (!total) return "conic-gradient(#e2e8f0 0 100%)";
    let cursor = 0;
    const stops = entries.map((e) => {
      const start = (cursor / total) * 360;
      cursor += e.v;
      const end = (cursor / total) * 360;
      return `${e.color} ${start}deg ${end}deg`;
    });
    return `conic-gradient(${stops.join(", ")})`;
  }, [entries, total]);

  return (
    <div className="flex items-center gap-4" data-testid="board-dial">
      <div
        className="relative w-28 h-28 rounded-full shrink-0"
        style={{ background: gradient }}
        aria-label="Category breakdown"
      >
        <div className="absolute inset-3 rounded-full bg-white flex flex-col items-center justify-center">
          <span className="text-xs opacity-60 uppercase tracking-widest">Total</span>
          <span className="font-display text-xl font-bold" style={{ color: "#065f46" }}>
            {total.toLocaleString()}
          </span>
        </div>
      </div>
      <div className="flex-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        {entries.length === 0 && (
          <div className="col-span-2 opacity-50">No categories logged yet.</div>
        )}
        {entries.slice(0, 8).map((e) => (
          <div key={e.k} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: e.color }} />
            <span className="opacity-80">{CATEGORY_META[e.k].label}</span>
            <span className="font-mono opacity-50 ml-auto">{e.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */
export default function SocialCapitalBoardPage() {
  const { handle: rawHandle } = useParams();
  const handle = (rawHandle || "").replace(/^@/, "");

  const [portfolio, setPortfolio] = useState(null);
  const [badges, setBadges] = useState(null);
  const [endorsement, setEndorsement] = useState(null);   // featured (most recent)
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!handle) return;
    let dead = false;
    setLoading(true); setNotFound(false);
    Promise.all([
      http.get(`/social-capital/portfolio/${encodeURIComponent(handle)}`).catch(() => null),
      http.get(`/badges/${encodeURIComponent(handle)}`).catch(() => null),
      http.get(`/endorsements/received/${encodeURIComponent(handle)}?limit=1`).catch(() => null),
    ]).then(([p, b, e]) => {
      if (dead) return;
      if (!p?.data) { setNotFound(true); setLoading(false); return; }
      setPortfolio(p.data);
      setBadges(b?.data || { items: [] });
      setEndorsement((e?.data?.items || [])[0] || null);
      setLoading(false);
    });
    return () => { dead = true; };
  }, [handle]);

  const share = async () => {
    const url = `${window.location.origin}/@${portfolio?.user?.handle || handle}`;
    if (navigator.share) {
      try { await navigator.share({ title: `${portfolio?.user?.name}'s Social Capital`, url }); return; } catch {/* fall through */}
    }
    try { await navigator.clipboard.writeText(url); toast.success("Board link copied."); }
    catch { toast.error("Could not copy link."); }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto py-24 text-center" data-testid="board-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-70" />
      </div>
    );
  }
  if (notFound || !portfolio) {
    return (
      <div className="max-w-2xl mx-auto py-24 text-center" data-testid="board-notfound">
        <h2 className="font-display text-3xl mb-2">No board yet</h2>
        <p className="text-sm opacity-70">
          We couldn&apos;t find a Social Capital board for <code>@{handle}</code>.
        </p>
      </div>
    );
  }

  const u = portfolio.user || {};
  const totalPts = u.social_capital || 0;
  const level = u.level || "Helper";
  const initials = (u.name || u.handle || "?").trim().charAt(0).toUpperCase();
  const ring = levelRingStyle(level);
  const earnedBadges = (badges?.items || []).filter((b) => b.name);
  const cats = portfolio.category_totals || {};

  return (
    <div className="max-w-2xl mx-auto px-5 py-8 print:py-0" data-testid="board-page">
      <SeoHead
        section="board"
        title={`${u.name || u.handle} — Social Capital Board`}
        description={`${level} · ${totalPts.toLocaleString()} lifetime points across ${earnedBadges.length} badges. Community-authored reputation on socialcapital.cc.`}
        url={`/@${u.handle || u.id}`}
      />

      {/* Frame */}
      <article
        className="relative overflow-hidden rounded-3xl border-2 shadow-xl bg-white"
        style={{ borderColor: ring.ring }}
        data-testid="board-card"
      >
        {/* Watermark */}
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-20 pointer-events-none"
             style={{ background: `radial-gradient(circle, ${ring.ring} 0%, transparent 70%)` }} />

        {/* Ribbon */}
        <div
          className="flex items-center justify-between px-6 py-3 text-xs uppercase tracking-[0.22em] font-semibold"
          style={{ background: ring.bg, color: "#fff" }}
        >
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4" />
            SocialCapital.cc
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <button onClick={share} data-testid="board-share"
                    className="rounded-full bg-white/20 hover:bg-white/30 transition-colors px-2.5 py-1 inline-flex items-center gap-1">
              <Share2 className="w-3 h-3" /> Share
            </button>
            <button onClick={() => window.print()} data-testid="board-print"
                    className="rounded-full bg-white/20 hover:bg-white/30 transition-colors px-2.5 py-1 inline-flex items-center gap-1">
              <Copy className="w-3 h-3" /> Print
            </button>
          </div>
        </div>

        {/* Identity */}
        <div className="relative px-6 sm:px-10 pt-8 pb-6 flex items-center gap-5">
          <div className="relative shrink-0">
            <div className="w-24 h-24 rounded-full p-1" style={{ background: ring.bg }}>
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center font-display text-3xl font-bold"
                   style={{ color: ring.ring }}>
                {initials}
              </div>
            </div>
            {/* Level stamp */}
            <div
              className="absolute -bottom-1 -right-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow"
              style={{ background: ring.bg, color: "#fff" }}
              data-testid="board-level"
            >
              {level}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest opacity-60 mb-0.5">Passport ID</div>
            <h1 className="font-display text-2xl sm:text-3xl leading-tight font-bold"
                data-testid="board-name">
              {u.name || u.handle || "Anonymous"}
            </h1>
            {u.handle && (
              <div className="text-xs font-mono opacity-70">@{u.handle}</div>
            )}
            {(u.profession || u.city) && (
              <div className="text-xs mt-1 opacity-80">
                {[u.profession, u.city, u.country].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="font-display text-4xl font-bold" style={{ color: ring.ring }}
                 data-testid="board-total">
              {totalPts.toLocaleString()}
            </div>
            <div className="text-[10px] uppercase tracking-widest opacity-60">Lifetime pts</div>
          </div>
        </div>

        {/* Category dial + intro */}
        <div className="px-6 sm:px-10 pb-6 border-t"
             style={{ borderColor: "rgba(0,0,0,0.06)" }}>
          <div className="pt-5 mb-2 text-xs uppercase tracking-widest opacity-60">
            Contribution breakdown
          </div>
          <CategoryDial totals={cats} />
        </div>

        {/* Trophy wall — earned badges only */}
        <div className="px-6 sm:px-10 py-5 border-t"
             style={{ borderColor: "rgba(0,0,0,0.06)", background: "#FEFCE8" }}
             data-testid="board-badges">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest opacity-70 flex items-center gap-1.5">
              <Trophy className="w-3 h-3" /> Trophy wall
            </div>
            <div className="text-[10px] font-mono opacity-50">
              {earnedBadges.length} earned
            </div>
          </div>
          {earnedBadges.length === 0 ? (
            <div className="text-sm opacity-60 italic" data-testid="board-badges-empty">
              No badges yet — the wall waits for their first contribution.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {earnedBadges.map((b) => {
                const Icon = ICONS[b.icon] || Award;
                const style = TIER_STYLE[b.tier] || TIER_STYLE.bronze;
                return (
                  <div
                    key={b.code}
                    className="rounded-xl bg-white border p-3 flex items-start gap-2.5"
                    style={{ borderColor: style.ring }}
                    data-testid={`board-badge-${b.code}`}
                  >
                    <div
                      className="w-10 h-10 rounded-lg inline-flex items-center justify-center text-white shrink-0"
                      style={{ background: style.bg }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[13px] font-semibold leading-tight">{b.name}</div>
                      <div className="text-[10px] opacity-70 mt-0.5">{b.description}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Featured endorsement */}
        {endorsement && (
          <div className="px-6 sm:px-10 py-5 border-t"
               style={{ borderColor: "rgba(0,0,0,0.06)" }}
               data-testid="board-endorsement">
            <div className="text-xs uppercase tracking-widest opacity-60 mb-2 flex items-center gap-1.5">
              <Heart className="w-3 h-3" /> Community says
            </div>
            <blockquote className="italic text-sm leading-relaxed"
                        style={{ color: "#0f172a" }}>
              &ldquo;{endorsement.message}&rdquo;
            </blockquote>
            <div className="mt-2 text-[11px] opacity-70">
              — <span className="font-medium">{endorsement.from_name || "Anonymous"}</span>
              {endorsement.category && (
                <> · <span style={{ color: CATEGORY_META[endorsement.category]?.color }}>
                  {CATEGORY_META[endorsement.category]?.label || endorsement.category}
                </span></>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 sm:px-10 py-4 border-t text-[10px] font-mono uppercase tracking-widest flex items-center justify-between"
             style={{ borderColor: "rgba(0,0,0,0.06)", color: "#64748b" }}>
          <span>socialcapital.cc/@{u.handle || u.id}</span>
          <span className="opacity-70">Powered by Social Pulse®</span>
        </div>
      </article>

      {/* Cross-links (hidden on print) */}
      <div className="mt-6 flex flex-wrap justify-center gap-3 print:hidden"
           data-testid="board-crosslinks">
        <Link to={`/passport/${u.handle || u.id}`}
              data-testid="board-view-passport"
              className="text-xs inline-flex items-center gap-1 rounded-full border px-3 py-1.5 hover:bg-black/[0.02]">
          View full passport <ArrowRight className="w-3 h-3" />
        </Link>
        <Link to="/why-we-build"
              className="text-xs inline-flex items-center gap-1 rounded-full border px-3 py-1.5 hover:bg-black/[0.02]">
          Why this exists <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}
